import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';

import { WellsService } from 'src/app/services/wells.service';
import { ProjectsService } from 'src/app/services/projects.service';
import { Well, WellSummary } from '@models/well.interface';
import { RigLookupDto } from '@models/project-lookups-dto.interface';
import { LookupDto } from '@models/lookup-dto.interface';

import { WellListComponent } from './well-list/well-list.component';
import { WellDetailComponent, WellSavedEvent } from './well-detail/well-detail.component';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '@shared/components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-wells-page',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    WellListComponent,
    WellDetailComponent
  ],
  templateUrl: './wells-page.component.html',
  styleUrl: './wells-page.component.scss'
})
export class WellsPageComponent implements OnInit {
  private readonly wellsService = inject(WellsService);
  private readonly projectsService = inject(ProjectsService);
  private readonly dialog = inject(MatDialog);

  protected wells = signal<WellSummary[]>([]);
  protected selectedWell = signal<Well | null>(null);
  protected selectedId = signal<number | null>(null);
  protected newlyCreatedId = signal<number | null>(null);
  protected isLoadingList = signal(false);
  protected isLoadingDetail = signal(false);

  protected rigs = signal<RigLookupDto[]>([]);
  protected businessUnits = signal<LookupDto[]>([]);
  protected isLoadingLookups = signal(true);

  ngOnInit(): void {
    this.loadList();
    this.loadLookups();
  }

  private loadLookups(): void {
    this.isLoadingLookups.set(true);
    this.projectsService.getLookups().subscribe({
      next: (lookups) => {
        this.rigs.set(lookups.rigs);
        this.businessUnits.set(lookups.businessUnits);
        this.isLoadingLookups.set(false);
      },
      error: () => this.isLoadingLookups.set(false)
    });
  }

  private loadList(selectId?: number, forceRefresh = false): void {
    this.isLoadingList.set(true);
    this.wellsService.getList(forceRefresh).subscribe({
      next: list => {
        this.wells.set(list);
        this.isLoadingList.set(false);

        // Re-select after refresh, or restore previous selection
        const targetId = selectId ?? this.selectedId();
        if (targetId !== null) {
          const stillPresent = list.find(w => w.id === targetId);
          if (stillPresent) {
            this.loadDetail(targetId, forceRefresh && selectId === targetId);
          } else {
            this.selectedWell.set(null);
            this.selectedId.set(null);
          }
        }
      },
      error: () => {
        this.isLoadingList.set(false);
      }
    });
  }

  private loadDetail(id: number, forceRefresh = false): void {
    this.isLoadingDetail.set(true);
    this.selectedId.set(id);
    this.wellsService.getById(id, forceRefresh).subscribe({
      next: well => {
        this.selectedWell.set(well);
        this.isLoadingDetail.set(false);
      },
      error: () => {
        this.selectedWell.set(null);
        this.isLoadingDetail.set(false);
      }
    });
  }

  protected onWellSelected(summary: WellSummary): void {
    this.newlyCreatedId.set(null);
    this.loadDetail(summary.id);
  }

  protected onAddClicked(): void {
    this.newlyCreatedId.set(null);
    // Create an unsaved "new" well shell
    const newWell: Well = {
      id: 0,
      name: '',
      prospectName: null,
      prospectNumber: null,
      field: null,
      blockNumber: null,
      api: null,
      chevNo: null,
      mudSupplierId: null,
      cementSupplierId: null,
      defaultRigId: null,
      createdOn: new Date().toISOString()
    };
    this.selectedWell.set(newWell);
    this.selectedId.set(null);
  }

  protected onWellSaved(event: WellSavedEvent): void {
    // Reload the list and re-select the saved well
    this.selectedId.set(event.id);
    this.newlyCreatedId.set(event.id);
    this.loadList(event.id, true);
  }

  protected onWellDeleted(wellId: number): void {
    const dialogRef = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData, boolean>(
      ConfirmationDialogComponent,
      {
        data: {
          title: 'Delete Well',
          primaryText: 'Are you sure you want to delete this well?',
          confirmText: 'Delete',
        },
        width: '420px',
        disableClose: true,
      }
    );
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.wellsService.delete(wellId).subscribe({
        next: () => {
          // Clear selection if the deleted well was selected
          if (this.selectedId() === wellId) {
            this.selectedWell.set(null);
            this.selectedId.set(null);
          }
          // Reload the list
          this.loadList();
        },
        error: () => {
          alert('Failed to delete well. Please try again.');
        }
      });
    });
  }
}
